import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { SelfUserClassificationEnum } from "../../../shared/constants/constants";
import { getSessionData } from "../../../utils/interceptors/localStorageService";

const RouteHistoryContext = createContext([]);

const MAX_HISTORY = 10;
const EXCLUDED_ROUTES = [
  "/",
  "/otp",
  "/forgot-password",
  "/validate-user",
  "/validate-user/security-features",
  "/login",
  "/admin/login",
  "/signup",
  "/authentication",
];

const isExcludedRoute = (path) =>
  EXCLUDED_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));

export const RouteHistoryProvider = ({ children }) => {
  const location = useLocation();
  const [history, setHistory] = useState([]);
  const historyRef = useRef([]);
  const loadedForUser = useRef(null);

  useEffect(() => {
    const session = getSessionData();
    const isAdmin =
      session?.userClassification ===
      SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value;
    if (!isAdmin) return;

    // Read the session fresh on every navigation so tracking isn't tied to a
    // mount-time race where the user id isn't populated yet.
    const uid = session?.id || session?.userId || "admin";

    // Hydrate from storage once per user.
    if (loadedForUser.current !== uid) {
      const saved = localStorage.getItem(`routeHistory:${uid}`);
      historyRef.current = saved ? JSON.parse(saved) : [];
      loadedForUser.current = uid;
      setHistory(historyRef.current);
    }

    if (isExcludedRoute(location.pathname)) return;

    // Store the full path including the query string (e.g. ?user=916) so that
    // navigating from history preserves route params, not just the pathname.
    const path = location.pathname + location.search;

    const updated = [
      path,
      ...historyRef.current.filter((entry) => entry !== path),
    ].slice(0, MAX_HISTORY);

    historyRef.current = updated;
    setHistory(updated);
    localStorage.setItem(`routeHistory:${uid}`, JSON.stringify(updated));
  }, [location.pathname, location.search]);

  return (
    <RouteHistoryContext.Provider value={history}>
      {children}
    </RouteHistoryContext.Provider>
  );
};

export const useRouteHistory = () => useContext(RouteHistoryContext);
